import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgClass} from '@angular/common';
import {NlrFormFieldHasErrorPipe} from '../../../../pipe/nlr-form-field-has-error.pipe';
import {NlrPatternDirective} from '../../../../directive/nlr-pattern.directive';
import {FormsModule} from '@angular/forms';
import {PlanModelService} from '../../../../service/plan-model.service';
import {PLAN_LODGEMENT_STAGE} from '../../../../constants';
import {equals} from '../../../../service/data-util';
import {PlanLodgementService} from '../../../../service/plan-lodgement.service';
import {SpatialUtilService} from '../../../../../design/services/spatial-util.service';
import {SpatialSettings} from '../../../../models/spatial-settings.model';
import {FirmSettingsService} from '../../../../service/firm-settings.service';
import {PlanPdfService} from '../../../../service/plan-pdf.service';
import {AlertModalService} from '../../alert-modal/alert-modal.service';
import {ConfirmationModalService} from '../../confirmation-modal/confirmation-modal.service';
import {EMPTY, map, Observable, of, tap} from 'rxjs';
import {PrintAreaService} from '../../../../service/print-area.service';
import {EnlargementService} from '../../../../../design/widgets/enlargement/services/enlargement.service';
import {NlrHasPermissionsPipe} from '../../../../pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../../../pipe/nlr-permissions-disabled-pipe';
import {switchMap} from 'rxjs/operators';
import {NgEventBus} from 'ng-event-bus';
import {CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';

@Component({
    selector: 'nlr-pdf-print-settings',
    imports: [
        NgClass,
        NlrFormFieldHasErrorPipe,
        NlrPatternDirective,
        FormsModule,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        CdkDropList,
        CdkDrag,
        CdkDropListGroup
    ],
    templateUrl: './pdf-print-settings.component.html',
    styleUrl: './pdf-print-settings.component.scss'
})
export class PdfPrintSettingsComponent implements OnInit {
    @Input()
    spatialReference: string;

    @Input()
    accessRestriction: boolean;

    @Output()
    closeEvent: EventEmitter<void> = new EventEmitter<void>();

    @Output()
    dismissEvent: EventEmitter<string> = new EventEmitter<string>();

    title = 'Pdf Print Settings';

    FULL_PAGE = 'Full Page';

    model: SpatialSettings;

    pristineModel: SpatialSettings;

    watermarkRegex = /^[- a-zA-Z0-9\\*/]+$/;

    maxWatermarkCharacters = 70;

    restrictedPrintOptions = false;

    pristinePageItemList = [];

    pageItemList = [];

    pageItemMap = {};

    pageItemIdsList: number[][] = [];

    showModel: boolean = false;

    showPageItemIdsList: boolean = false;

    showPageItemList: boolean = false;

    showPristinePageItemList: boolean = false;

    showPlan: boolean = false;

    constructor(
        private alertModalService: AlertModalService,
        private confirmationModalService: ConfirmationModalService,
        private eventBus: NgEventBus,
        private enlargementService: EnlargementService,
        private firmSettingsService: FirmSettingsService,
        private planPdfService: PlanPdfService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private printAreaService: PrintAreaService,
        private spatialUtilService: SpatialUtilService
    ) {
        this.restrictedPrintOptions =
            this.getPlanModel().planLodgementStage !== PLAN_LODGEMENT_STAGE.IN_DESIGN &&
            this.getPlanModel().planLodgementStage !== PLAN_LODGEMENT_STAGE.READY_FOR_QA;
    }

    ngOnInit() {
        const planModel = this.getPlanModel();

        this.model = {
            watermarkText: (!this.restrictedPrintOptions && planModel.spatialSettings.watermarkText) || '',
            printHiddenFeatures: !this.restrictedPrintOptions && !!planModel.spatialSettings.printHiddenFeatures,
            printEnlargementExtents: !this.restrictedPrintOptions && !!planModel.spatialSettings.printEnlargementExtents
        } as SpatialSettings;

        if (!this.restrictedPrintOptions) {
            if (!this.model.watermarkText && !planModel.spatialSettings.overrideDefaultWatermark) {
                const subscription = this.firmSettingsService.getDefaultPdfWatermark().subscribe({
                    next: defaultPdfWatermark => (this.model.watermarkText = defaultPdfWatermark),
                    error: reason => console.warn(this.title, 'ngOnInit - firmSettingsService#getDefaultPdfWatermark error: ', reason),
                    complete: () => console.debug(this.title, 'ngOnInit - Default PDF watermark set to: ', this.model.watermarkText)
                });

                this.eventBus.cast('nlr:busy', subscription);
            }
        }

        this.setPageItemList(this.spatialUtilService.getEnlargements());

        this.pristineModel = structuredClone(this.model);
    }

    getPlanModel() {
        return this.planModelService.getModel();
    }

    getPaperSizeLookup() {
        return this.printAreaService.getPaperSizeLookup();
    }

    getPaperSize() {
        return this.getPaperSizeLookup().paperSize;
    }

    getReferenceScaleLookup() {
        return this.printAreaService.getReferenceScaleLookup();
    }

    getReferenceScale() {
        return this.getReferenceScaleLookup().referenceScale;
    }

    hasPrintAreaGeometry() {
        return !!this.printAreaService.getPrintAreaGeometry();
    }

    toJson(value: object) {
        return JSON.stringify(value, null, 4);
    }

    private modelIsPristine() {
        return equals(this.model, this.pristineModel);
    }

    private pageItemListIsPristine() {
        return equals(this.pageItemList, this.pristinePageItemList);
    }

    private isFullPageItem(pageItemId: number) {
        return this.pageItemMap[pageItemId].attributes.fullpage === this.FULL_PAGE;
    }

    private getMaxPageItemsInternal(pageItemIds: number[]) {
        return pageItemIds.length === 1 && this.isFullPageItem(pageItemIds[0]) ? 1 : 4;
    }

    private isPageFull(pageItemIds: number[]) {
        return pageItemIds && pageItemIds.length === this.getMaxPageItemsInternal(pageItemIds);
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    private setPageItemList(newPageItemList: any[]) {
        console.debug(this.title, 'setPageItemList', newPageItemList);

        this.pageItemList.splice(
            0,
            this.pageItemList.length,
            ...newPageItemList
                .map(v => ({attributes: structuredClone(v.attributes), geometry: structuredClone(v.geometry)}))
                .sort((pageItem1, pageItem2) => pageItem1.attributes.id - pageItem2.attributes.id)
        );

        // DCX-5106: update pristine to keep enlargements retrieved from database
        this.pristinePageItemList = structuredClone(this.pageItemList);

        this.pageItemMap = this.pageItemList.reduce((acc, pageItem) => {
            acc[pageItem.attributes.id] = pageItem;
            return acc;
        }, {});

        const unorderedPageItemsList = this.pageItemList
            .filter(pageItem => pageItem.attributes.pageorder === null)
            .reduce((acc, pageItem, pageItemIndex, arr) => {
                if (
                    pageItemIndex === 0 ||
                    pageItem.attributes.fullpage === this.FULL_PAGE ||
                    arr[pageItemIndex - 1].attributes.fullpage === this.FULL_PAGE ||
                    acc[acc.length - 1].length === 4
                ) {
                    acc.push([pageItem]);
                } else {
                    //Current item and previous item are 4 per page and current page has less than 4 items so push current item into current page.
                    acc[acc.length - 1].push(pageItem);
                }
                return acc;
            }, []);

        const orderedPageItems = this.pageItemList.filter(pageItem => pageItem.attributes.pageorder !== null);

        const orderedPageItemMap = orderedPageItems.reduce((acc, pageItem) => {
            if (!acc[pageItem.attributes.pageorder]) {
                acc[pageItem.attributes.pageorder] = [];
            }
            acc[pageItem.attributes.pageorder].push(pageItem);
            acc[pageItem.attributes.pageorder].sort((pi1, pi2) => pi1.attributes.itemorder - pi2.attributes.itemorder);
            return acc;
        }, {});

        const pageItemsList = Object.keys(orderedPageItemMap).map(k => orderedPageItemMap[k]);

        pageItemsList.push(...unorderedPageItemsList);

        this.pageItemIdsList.splice(
            0,
            this.pageItemIdsList.length,
            ...pageItemsList.reduce((acc, pageItems) => {
                acc.push(pageItems.map(pageItem => pageItem.attributes.id));
                return acc;
            }, [])
        );

        this.syncSortableOptions();
    }

    private syncSortableOptions() {
        console.debug(this.title, 'syncSortableOptions');

        const pageItemIdsList = structuredClone(this.pageItemIdsList).filter(pageItemIds => pageItemIds.length);

        const gotMultiItemPages = pageItemIdsList.some(pageItemIds => pageItemIds.length > 1);

        if (gotMultiItemPages) {
            //Multi-item pages present, so need empty row to drag items to from multi-item pages.
            pageItemIdsList.push([]);
        }

        this.pageItemIdsList.splice(0, this.pageItemIdsList.length, ...pageItemIdsList);

        this.pageItemIdsList.forEach((pageItemIds, pageItemIdsIndex) => {
            pageItemIds.forEach((pageItemId: number, pageItemIdIndex: number) => {
                const pageItem = this.pageItemMap[pageItemId];
                pageItem.attributes.pageorder = pageItemIdsIndex;
                pageItem.attributes.itemorder = pageItemIdIndex;
            });
        });
    }

    private printInternal() {
        console.debug(this.title, 'printInternal');

        return this.planPdfService.generateAndDisplayPdf(
            {
                spatialReference: this.spatialReference,
                referenceScale: this.getReferenceScale(),
                paperSize: this.getPaperSize(),
                watermarkText: this.model.watermarkText,
                printHiddenFeatures: this.model.printHiddenFeatures,
                printEnlargementExtents: this.model.printEnlargementExtents
            },
            false
        );
    }

    private saveInternal() {
        console.debug(this.title, 'saveInternal');

        const planModel = this.getPlanModel();

        const newSpatialSettings = Object.assign({}, planModel.spatialSettings ? structuredClone(planModel.spatialSettings) : {}, {
            watermarkText: this.model.watermarkText,
            printHiddenFeatures: this.model.printHiddenFeatures,
            printEnlargementExtents: this.model.printEnlargementExtents,
            overrideDefaultWatermark: true
        });

        let observable: Observable<any> = of(null);

        if (!equals(newSpatialSettings, planModel.spatialSettings)) {
            planModel.spatialSettings = newSpatialSettings;

            observable = this.planLodgementService
                .save({reconcileSpatialFeatures: false, showAlertOnSave: false})
                .pipe(tap(() => (this.pristineModel = structuredClone(this.model))));
        }

        const filteredPageItemList = this.pageItemList.filter((pageItem, pageItemIndex) => {
            return !equals(pageItem.attributes, this.pristinePageItemList[pageItemIndex].attributes);
        });

        if (filteredPageItemList.length) {
            observable = observable.pipe(
                switchMap(() => this.enlargementService.updateEnlargementFeatures(filteredPageItemList)),
                tap(() => (this.pristinePageItemList = structuredClone(this.pageItemList)))
            );
        }

        return observable;
    }

    getPageItemDisplayName(pageItemId: number) {
        const pageItem = this.pageItemMap[pageItemId];
        return pageItem.attributes.name + ' - ' + pageItem.attributes.fullpage;
    }

    isEmptyLastPage(pageIndex: number) {
        return pageIndex === this.pageItemIdsList.length - 1 && this.pageItemIdsList[pageIndex].length === 0;
    }

    hasEmptyLastPage() {
        return this.pageItemIdsList.length && this.pageItemIdsList[this.pageItemIdsList.length - 1].length === 0;
    }

    getMaxPageItems(pageItemIds: number[]) {
        return this.getMaxPageItemsInternal(pageItemIds);
    }

    resetPageOrderDisabled() {
        return this.pageItemListIsPristine();
    }

    pageReorderingDisabled() {
        return this.accessRestriction;
    }

    resetPageOrder() {
        console.debug(this.title, 'resetPageOrder');

        this.setPageItemList(this.pristinePageItemList);
    }

    getWatermarkCharactersRemaining() {
        if (!this.model.watermarkText) {
            return this.maxWatermarkCharacters;
        }
        const newLineCount = this.model.watermarkText.split('\n').length - 1;
        const characterCount = this.model.watermarkText.length - newLineCount;
        return this.maxWatermarkCharacters - characterCount;
    }

    showWatermark() {
        return this.planPdfService.showWatermark(this.planModelService.getModel());
    }

    pageSizeChanged() {
        console.debug(this.title, 'pageSizeChanged');
    }

    referenceScaleChanged() {
        console.debug(this.title, 'referenceScaleChanged');
    }

    gotPrerequisites() {
        return this.model && this.getPlanModel();
    }

    plottingReferenceScaleDisabled() {
        return this.accessRestriction;
    }

    print() {
        console.debug(this.title, 'print');

        const subscription = this.printInternal().subscribe(() => this.closeEvent.emit());

        this.eventBus.cast('nlr:busy', subscription);
    }

    // disabled when no pages or restricting access from hamburger menu
    pageOrderDisabled() {
        return this.accessRestriction || this.pageItemIdsList.length;
    }

    save() {
        console.debug(this.title, 'save');

        const subscription = this.saveInternal().subscribe({
            next: () => {
                this.alertModalService.createAlertMessage('Settings saved successfully.', 'Ok', 'Settings saved', 'success');
            },
            error: reason => {
                this.alertModalService.createAlertMessage(reason, 'Ok');
            }
        });

        this.eventBus.cast('nlr:busy', subscription);
    }

    saveDisabled() {
        return this.modelIsPristine() && this.pageItemListIsPristine();
    }

    saveAndPrint() {
        console.debug(this.title, 'saveAndPrint');

        let observable: Observable<any> = this.modelIsPristine() && this.pageItemListIsPristine() ? of(null) : this.saveInternal();

        if (this.spatialUtilService.hasEdits()) {
            observable = observable.pipe(
                switchMap(() =>
                    this.confirmationModalService.showModal({
                        title: 'Save plan?',
                        confirmationText: 'The plan must be saved before it can be printed.<br>Do you want to save the plan?',
                        iconType: 'info'
                    })
                ),
                switchMap(action => {
                    if (action === 'yes') {
                        return this.spatialUtilService.save().pipe(switchMap(() => this.printInternal()));
                    }
                    return EMPTY;
                })
            );
        } else {
            observable = observable.pipe(switchMap(() => this.printInternal()));
        }

        const subscription = observable.subscribe(() => {
            subscription.unsubscribe();
            this.closeEvent.emit();
        });

        this.eventBus.cast('nlr:busy', subscription);
    }

    cancel() {
        console.debug(this.title, 'cancel');

        this.dismissEvent.emit('Cancelled');
    }

    togglePrintHiddenFeaturesIfAllowed() {
        console.debug(this.title, 'togglePrintHiddenFeaturesIfAllowed');

        if (!this.printHiddenFeaturesDisabled()) {
            this.model.printHiddenFeatures = !this.model.printHiddenFeatures;
        }
    }

    togglePrintEnlargementExtentsIfAllowed() {
        console.debug(this.title, 'togglePrintEnlargementExtentsIfAllowed');

        this.model.printEnlargementExtents = !this.model.printEnlargementExtents;
    }

    printHiddenFeaturesDisabled() {
        return this.restrictedPrintOptions;
    }

    paperSizeDisabled() {
        return this.accessRestriction;
    }

    pageItemListAttributes() {
        return this.pageItemList.map(v => v.attributes);
    }

    pristinePageItemListAttributes() {
        return this.pristinePageItemList.map(v => v.attributes);
    }

    dropList(event: CdkDragDrop<number[][]>) {
        if (
            event.currentIndex === event.previousIndex ||
            /* Cancel update if attempting to move the empty page */
            event.previousContainer.data.length === 0 ||
            /* Cancel update if attempting to move a page after the empty page */
            (this.hasEmptyLastPage() && event.currentIndex === event.previousContainer.data.length - 1)
        ) {
            return;
        }
        moveItemInArray(this.pageItemIdsList, event.previousIndex, event.currentIndex);

        this.syncSortableOptions();
    }

    dropItem(event: CdkDragDrop<number[]>) {
        const sourceData = event.previousContainer.data;
        const targetData = event.container.data;

        if (equals(sourceData, targetData) ? event.currentIndex === event.previousIndex : this.isPageFull(targetData)) {
            return;
        }

        transferArrayItem(sourceData, targetData, event.previousIndex, event.currentIndex);

        this.syncSortableOptions();
    }
}
